import React, { useCallback, useEffect, useState } from 'react';

import { Painel } from '../../../../components/Painel';
import { useToast } from '../../../../hooks/toast';
import {
  AdminPlansService,
  IAdminPlan,
  IAdminPlanDetail,
} from '../../../../services/AdminPlansService';

import {
  Container,
  DetailColumn,
  EmptyDetail,
  FeatureCode,
  FeatureGroupTitle,
  FeatureInfo,
  FeatureList,
  FeatureName,
  FeatureToggleRow,
  FieldGroup,
  FieldLabel,
  FormGrid,
  Header,
  Layout,
  ListColumn,
  NumericRow,
  Panel,
  PanelSubtitle,
  PanelTitle,
  PageSubtitle,
  PageTitle,
  PlanRow,
  PlanRowName,
  PlanRowPrice,
  RowSaveButton,
  SaveButton,
  TextArea,
  TextInput,
  ToggleSwitch,
  UnlimitedLabel,
  ValueCell,
  ValueInput,
} from './styles';

// Os 3 únicos packages de quantidade (limite numérico, NULL = ilimitado)
// -- todo o resto do catálogo é boolean (0/1 = bloqueado/liberado). Sem
// coluna de tipo em `features`, então a distinção fica aqui mesmo (só
// muda se um package numérico novo for criado, o que não é feito pela
// UI -- ver FEATURE-PACKAGES-PLAN.md).
const NUMERIC_FEATURE_CODES = ['users', 'exercises', 'news'];

const AdminPlans: React.FC = () => {
  const adminPlansService = new AdminPlansService();
  const { addToast } = useToast();

  const [plans, setPlans] = useState<IAdminPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>();
  const [detail, setDetail] = useState<IAdminPlanDetail>();
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingFeatureId, setSavingFeatureId] = useState<string | null>(null);

  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');

  // Valor em edição por feature numérica -- string pra permitir campo
  // vazio durante a digitação; `null` marca "ilimitado" (checkbox), não
  // "vazio". Só sincroniza de volta com o valor salvo depois de um save
  // bem-sucedido, então digitar e não salvar não perde o valor real.
  // Features booleanas não passam por aqui -- o toggle salva na hora.
  const [editedValues, setEditedValues] = useState<
    Record<string, { value: string; unlimited: boolean }>
  >({});

  useEffect(() => {
    adminPlansService
      .getPlans()
      .then(response => {
        setPlans(response.data);
        if (response.data.length) {
          setSelectedPlanId(response.data[0].id);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPlanDetail = useCallback((plan_id: string) => {
    setLoadingDetail(true);
    adminPlansService
      .getPlan(plan_id)
      .then(response => {
        setDetail(response.data);
        setPrice(String(response.data.subscription.price));
        setDescription(response.data.subscription.description);
        setEditedValues(
          Object.fromEntries(
            response.data.features
              .filter(feature => NUMERIC_FEATURE_CODES.includes(feature.code))
              .map(feature => [
                feature.feature_id,
                {
                  value: feature.value === null ? '' : String(feature.value),
                  unlimited: feature.value === null,
                },
              ]),
          ),
        );
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Não foi possível carregar o plano.',
        });
      })
      .finally(() => setLoadingDetail(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPlanId) {
      getPlanDetail(selectedPlanId);
    }
  }, [selectedPlanId, getPlanDetail]);

  const handleSavePlan = useCallback(() => {
    if (!selectedPlanId) return;

    setSavingPlan(true);
    adminPlansService
      .updatePlan(selectedPlanId, {
        price: Number(price),
        description,
      })
      .then(response => {
        setDetail(current =>
          current ? { ...current, subscription: response.data } : current,
        );
        setPlans(current =>
          current.map(plan =>
            plan.id === selectedPlanId ? { ...plan, ...response.data } : plan,
          ),
        );
        addToast({ type: 'success', title: 'Plano atualizado.' });
      })
      .catch(() => {
        addToast({ type: 'error', title: 'Não foi possível salvar o plano.' });
      })
      .finally(() => setSavingPlan(false));
  }, [selectedPlanId, price, description, addToast]);

  const handleToggleBoolean = useCallback(
    (feature_id: string, granted: boolean) => {
      if (!selectedPlanId) return;

      const value = granted ? 1 : 0;

      setSavingFeatureId(feature_id);
      adminPlansService
        .updateFeatureValue(selectedPlanId, feature_id, value)
        .then(() => {
          setDetail(current =>
            current
              ? {
                ...current,
                features: current.features.map(feature =>
                  feature.feature_id === feature_id
                    ? { ...feature, value }
                    : feature,
                ),
              }
              : current,
          );
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: 'Não foi possível salvar a feature.',
          });
        })
        .finally(() => setSavingFeatureId(null));
    },
    [selectedPlanId, addToast],
  );

  const handleFeatureValueChange = useCallback(
    (feature_id: string, value: string) => {
      setEditedValues(current => ({
        ...current,
        [feature_id]: { ...current[feature_id], value },
      }));
    },
    [],
  );

  const handleFeatureUnlimitedChange = useCallback(
    (feature_id: string, unlimited: boolean) => {
      setEditedValues(current => ({
        ...current,
        [feature_id]: { ...current[feature_id], unlimited },
      }));
    },
    [],
  );

  const isFeatureDirty = useCallback(
    (feature_id: string): boolean => {
      const saved = detail?.features.find(f => f.feature_id === feature_id);
      const edited = editedValues[feature_id];

      if (!saved || !edited) return false;

      const savedValue = saved.value === null ? '' : String(saved.value);
      const savedUnlimited = saved.value === null;

      return (
        edited.unlimited !== savedUnlimited ||
        (!edited.unlimited && edited.value !== savedValue)
      );
    },
    [detail, editedValues],
  );

  const handleSaveNumericFeature = useCallback(
    (feature_id: string) => {
      if (!selectedPlanId) return;

      const edited = editedValues[feature_id];
      const value = edited.unlimited ? null : Number(edited.value) || 0;

      setSavingFeatureId(feature_id);
      adminPlansService
        .updateFeatureValue(selectedPlanId, feature_id, value)
        .then(() => {
          setDetail(current =>
            current
              ? {
                ...current,
                features: current.features.map(feature =>
                  feature.feature_id === feature_id
                    ? { ...feature, value }
                    : feature,
                ),
              }
              : current,
          );
          addToast({ type: 'success', title: 'Feature atualizada.' });
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: 'Não foi possível salvar a feature.',
          });
        })
        .finally(() => setSavingFeatureId(null));
    },
    [selectedPlanId, editedValues, addToast],
  );

  const numericFeatures =
    detail?.features.filter(feature =>
      NUMERIC_FEATURE_CODES.includes(feature.code),
    ) || [];
  const booleanFeatures =
    detail?.features.filter(
      feature => !NUMERIC_FEATURE_CODES.includes(feature.code),
    ) || [];

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>Gestão de planos</PageTitle>
            <PageSubtitle>
              Preço, nível e features concedidas por cada plano da plataforma
            </PageSubtitle>
          </div>
        </Header>

        <Layout>
          <ListColumn>
            {plans.map(plan => (
              <PlanRow
                key={plan.id}
                type="button"
                active={plan.id === selectedPlanId}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <PlanRowName>{plan.name}</PlanRowName>
                <PlanRowPrice>
                  {plan.price > 0 ? `R$ ${plan.price.toFixed(2)}` : 'Gratuito'}
                </PlanRowPrice>
              </PlanRow>
            ))}
          </ListColumn>

          <DetailColumn>
            {!detail && (
              <EmptyDetail>
                {loadingDetail ? 'Carregando...' : 'Selecione um plano.'}
              </EmptyDetail>
            )}

            {detail && (
              <>
                <Panel>
                  <PanelTitle>Dados do plano</PanelTitle>
                  <PanelSubtitle>
                    Nome não é editável aqui. É usado como chave em código e
                    seeds (ex. fallback do plano de teste).
                  </PanelSubtitle>

                  <FormGrid>
                    <FieldGroup>
                      <FieldLabel htmlFor="plan-description">
                        Descrição
                      </FieldLabel>
                      <TextArea
                        id="plan-description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                      />
                    </FieldGroup>

                    <FieldGroup>
                      <FieldLabel htmlFor="plan-price">Preço (R$)</FieldLabel>
                      <TextInput
                        id="plan-price"
                        type="number"
                        step="0.01"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                      />
                    </FieldGroup>
                  </FormGrid>

                  <SaveButton
                    type="button"
                    name="save-plan"
                    value="Salvar dados do plano"
                    loading={savingPlan}
                    onClick={handleSavePlan}
                    style={{ marginTop: 16 }}
                  />
                </Panel>

                <Panel>
                  <PanelTitle>Features concedidas</PanelTitle>
                  <PanelSubtitle>
                    Marque quais features fazem parte deste plano por padrão.
                    Pacotes de quantidade (alunos, exercícios, avisos) usam um
                    número em vez de liga/desliga. Marque &quot;Ilimitado&quot;
                    pra não ter limite (ex. Performance).
                  </PanelSubtitle>

                  <FeatureGroupTitle>Quantidade</FeatureGroupTitle>
                  <FeatureList>
                    {numericFeatures.map(feature => {
                      const edited = editedValues[feature.feature_id] || {
                        value: '',
                        unlimited: false,
                      };
                      const dirty = isFeatureDirty(feature.feature_id);

                      return (
                        <NumericRow key={feature.feature_id}>
                          <FeatureInfo>
                            <FeatureName>{feature.nome}</FeatureName>
                            <FeatureCode>{feature.code}</FeatureCode>
                          </FeatureInfo>

                          <ValueCell>
                            <ValueInput
                              type="number"
                              disabled={edited.unlimited}
                              value={edited.unlimited ? '' : edited.value}
                              onChange={e =>
                                handleFeatureValueChange(
                                  feature.feature_id,
                                  e.target.value,
                                )
                              }
                            />
                            <UnlimitedLabel>
                              <input
                                type="checkbox"
                                checked={edited.unlimited}
                                onChange={e =>
                                  handleFeatureUnlimitedChange(
                                    feature.feature_id,
                                    e.target.checked,
                                  )
                                }
                              />
                              Ilimitado
                            </UnlimitedLabel>
                            <RowSaveButton
                              type="button"
                              dirty={dirty}
                              disabled={
                                !dirty || savingFeatureId === feature.feature_id
                              }
                              title="Salvar"
                              onClick={() =>
                                handleSaveNumericFeature(feature.feature_id)
                              }
                            >
                              ✓
                            </RowSaveButton>
                          </ValueCell>
                        </NumericRow>
                      );
                    })}
                  </FeatureList>

                  <FeatureGroupTitle>Liga/desliga</FeatureGroupTitle>
                  <FeatureList>
                    {booleanFeatures.map(feature => (
                      <FeatureToggleRow key={feature.feature_id}>
                        <FeatureInfo>
                          <FeatureName>{feature.nome}</FeatureName>
                          <FeatureCode>{feature.code}</FeatureCode>
                        </FeatureInfo>

                        <ToggleSwitch
                          type="checkbox"
                          checked={!!feature.value && feature.value > 0}
                          disabled={savingFeatureId === feature.feature_id}
                          onChange={e =>
                            handleToggleBoolean(
                              feature.feature_id,
                              e.target.checked,
                            )
                          }
                        />
                      </FeatureToggleRow>
                    ))}
                  </FeatureList>
                </Panel>
              </>
            )}
          </DetailColumn>
        </Layout>
      </Container>
    </Painel>
  );
};

export { AdminPlans };
